import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, Lock, Pencil, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PERIODS = ["Trimestre 1", "Trimestre 2", "Trimestre 3"];

export default function ClassGradesPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [classes, setClasses] = useState([]);
  const [className, setClassName] = useState("");
  const [period, setPeriod] = useState(PERIODS[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editDlg, setEditDlg] = useState(null); // { grade, student_name, mode: 'edit'|'adjust' }
  const [editForm, setEditForm] = useState({ value: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const [perms, setPerms] = useState([]);
  const canValidate = perms.includes("grades.validate");
  const isTeacher = user?.role === "teacher";

  useEffect(() => {
    api.get("/rbac/my-permissions").then(r => setPerms(r.data.permissions || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/pedagogy/my-classes").then(({ data }) => {
      setClasses(data.classes || []);
      const preset = location.state?.class_name;
      if (preset && (data.classes || []).some(c => c.name === preset)) setClassName(preset);
      else if ((data.classes || []).length === 1) setClassName(data.classes[0].name);
    }).catch(() => {});
  }, []);

  const load = async () => {
    if (!className) return;
    setLoading(true);
    try {
      const { data } = await api.get("/pedagogy/class-grades", { params: { class_name: className, period } });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [className, period]);

  const validateAll = async () => {
    if (!window.confirm(`Valider officiellement TOUTES les notes de ${className} (${period}) ? Les enseignants ne pourront plus les modifier sans votre approbation.`)) return;
    try {
      const { data } = await api.post("/pedagogy/grades/validate", { class_name: className, period });
      toast.success(`${data.validated_count} note(s) validée(s) et verrouillée(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openEdit = (g, studentName) => {
    const mode = g.validated && isTeacher ? "adjust" : "edit";
    setEditForm({ value: g.value, reason: "" });
    setEditDlg({ grade: g, student_name: studentName, mode });
  };

  const submitEdit = async () => {
    const g = editDlg.grade;
    setSaving(true);
    try {
      if (editDlg.mode === "adjust") {
        if (!editForm.reason.trim()) { toast.error("Le motif est obligatoire"); setSaving(false); return; }
        await api.post("/pedagogy/grade-adjustments", {
          grade_id: g.id, new_value: parseFloat(editForm.value), reason: editForm.reason,
        });
        toast.success("Demande envoyée au Directeur pour approbation");
      } else {
        await api.put(`/pedagogy/grades/${g.id}`, { value: parseFloat(editForm.value) });
        toast.success("Note modifiée (historisée)");
      }
      setEditDlg(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="class-grades-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><BookOpen className="h-3 w-3" /><span>NOTES PAR CLASSE</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">{isTeacher ? "Notes de mes classes" : "Notes des classes"}</h1>
          <p className="text-slate-500 mt-1">Élèves, notes par matière et période, moyennes automatiques et observations.</p>
        </div>
        {canValidate && className && (
          <Button onClick={validateAll} className="bg-[#166534] text-white gap-2" data-testid="validate-grades-btn">
            <ShieldCheck className="h-4 w-4" />Valider officiellement ({period})
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
        <div>
          <Label className="text-xs uppercase text-slate-500">Classe</Label>
          <Select value={className} onValueChange={setClassName}>
            <SelectTrigger data-testid="cg-class"><SelectValue placeholder="— Choisir —" /></SelectTrigger>
            <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase text-slate-500">Période</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="cg-period"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {loading && <div className="text-center py-10 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}

      {!loading && data && data.students.map(row => (
        <div key={row.student.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`cg-student-${row.student.id}`}>
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
            <div className="font-heading font-bold text-[#1E3A8A]">{row.student.first_name} {row.student.last_name} <span className="text-xs font-normal text-slate-400">{row.student.matricule}</span></div>
            <div className="flex items-center gap-3">
              {row.subject_averages.map(a => (
                <span key={a.subject} className="text-xs text-slate-500">{a.subject} <span className="text-slate-400">(coef {a.coefficient})</span> : <strong className={a.average >= 10 ? "text-[#166534]" : "text-red-600"}>{a.average}/20</strong></span>
              ))}
              {row.general_average !== null && (
                <span className={`text-sm font-extrabold px-2 py-0.5 rounded-full ${row.general_average >= 10 ? "bg-green-50 text-[#166534]" : "bg-red-50 text-red-600"}`} data-testid={`cg-avg-${row.student.id}`}>
                  Moy. pondérée {row.general_average}/20
                </span>
              )}
            </div>
          </div>
          {row.grades.length === 0 ? (
            <div className="px-5 py-4 text-sm text-slate-400">Aucune note sur cette période.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Matière</th>
                  <th className="px-4 py-2 text-right">Note</th>
                  <th className="px-4 py-2 text-left">Observation</th>
                  <th className="px-4 py-2 text-center">Statut</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {row.grades.map(g => (
                  <tr key={g.id} className="border-t border-slate-100" data-testid={`cg-grade-${g.id}`}>
                    <td className="px-4 py-2">{g.subject}</td>
                    <td className={`px-4 py-2 text-right font-bold tabular-nums ${(g.value / (g.max_value || 20)) * 20 >= 10 ? "text-[#166534]" : "text-red-600"}`}>{g.value}/{g.max_value || 20}</td>
                    <td className="px-4 py-2 text-xs text-slate-500 max-w-xs truncate">{g.comment || "—"}</td>
                    <td className="px-4 py-2 text-center">
                      {g.validated ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-[#1E3A8A] font-semibold"><Lock className="h-3 w-3" />Validée</span>
                      ) : <span className="text-xs text-slate-400">Provisoire</span>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {(isTeacher || canValidate) && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(g, `${row.student.first_name} ${row.student.last_name}`)} data-testid={`cg-edit-${g.id}`}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
      {!loading && data && data.students.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500">Aucun élève dans cette classe.</div>
      )}

      {/* Edit / Adjustment dialog */}
      <Dialog open={!!editDlg} onOpenChange={(o) => !o && setEditDlg(null)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              {editDlg?.mode === "adjust" ? "Demande de réajustement" : "Modifier la note"} — {editDlg?.student_name}
            </DialogTitle>
          </DialogHeader>
          {editDlg && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <strong>{editDlg.grade.subject}</strong> ({editDlg.grade.period}) — note actuelle : <strong>{editDlg.grade.value}/{editDlg.grade.max_value || 20}</strong>
              </div>
              {editDlg.mode === "adjust" && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-800">
                  Cette note est validée officiellement. Votre demande sera soumise au Directeur pour approbation, avec traçabilité complète.
                </div>
              )}
              <div>
                <Label>Nouvelle note (sur {editDlg.grade.max_value || 20}) *</Label>
                <Input type="number" step="0.25" min="0" max={editDlg.grade.max_value || 20} value={editForm.value} onChange={e => setEditForm({ ...editForm, value: e.target.value })} data-testid="cg-new-value" />
              </div>
              {editDlg.mode === "adjust" && (
                <div>
                  <Label>Motif du réajustement *</Label>
                  <Textarea rows={3} value={editForm.reason} onChange={e => setEditForm({ ...editForm, reason: e.target.value })} placeholder="Ex : erreur de report depuis la copie, points d'un exercice oubliés..." data-testid="cg-reason" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDlg(null)}>Annuler</Button>
            <Button onClick={submitEdit} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="cg-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editDlg?.mode === "adjust" ? "Envoyer au Directeur" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
